import {useLazyQuery} from "@apollo/client";
import {useCallback, useState} from "react";
import {FieldValues, UseFormSetValue, useFormContext} from "react-hook-form";

import {GQL_STATUS_PAGE_BRAND_LOOKUP, StatusPageBrandLookupResult} from "@/lib/queries/statuspages.ts";
import {StatusPageBrandLookup} from "@/lib/types/statuspages.ts";

export const WEBSITE_URL_FIELD = "company_website_url";

export type BrandLookupStatus = "idle" | "loading" | "applied" | "empty" | "error";

const base64ToFile = (base64: string, filename: string, contentType: string): File => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, {type: contentType});
};

const toFileList = (base64: string, filename: string, contentType: string | null): FileList => {
  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(base64ToFile(base64, filename, contentType || "image/png"));
  return dataTransfer.files;
};

const applyBrandToForm = (brand: StatusPageBrandLookup, setValue: UseFormSetValue<FieldValues>) => {
  if (brand.logo_base64 && brand.logo_filename) {
    setValue("logo", toFileList(brand.logo_base64, brand.logo_filename, brand.logo_content_type), {
      shouldDirty: true,
    });
  }
  if (brand.favicon_base64 && brand.favicon_filename) {
    setValue("favicon", toFileList(brand.favicon_base64, brand.favicon_filename, brand.favicon_content_type), {
      shouldDirty: true,
    });
  }
  if (brand.suggested_header_bg_color_hex) {
    setValue("custom_header_bg_color_hex", brand.suggested_header_bg_color_hex, {shouldDirty: true});
  }
  if (brand.suggested_header_text_color_hex) {
    setValue("custom_header_text_color_hex", brand.suggested_header_text_color_hex, {shouldDirty: true});
  }
};

// Drives the Brandfetch "auto-detect brand assets" action for the status page Website URL field:
// fetches brand data for the entered URL and applies the logo + header colors to the form fields.
export const useBrandLookup = () => {
  const {getValues, setValue} = useFormContext();
  const [status, setStatus] = useState<BrandLookupStatus>("idle");
  // The URL the current status describes, so the UI can hide it once the field no longer matches.
  const [queriedUrl, setQueriedUrl] = useState("");
  const [runQuery] = useLazyQuery<StatusPageBrandLookupResult>(GQL_STATUS_PAGE_BRAND_LOOKUP, {
    fetchPolicy: "no-cache",
  });

  const detect = useCallback(
    async (url: string) => {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        return;
      }
      setQueriedUrl(trimmedUrl);
      setStatus("loading");
      // The lookup can take several seconds; if the user edits the URL while it is in flight, the
      // result is stale — discard it rather than applying the old brand to the changed field.
      const isStale = () => (getValues(WEBSITE_URL_FIELD) || "").trim() !== trimmedUrl;
      try {
        const response = await runQuery({variables: {url: trimmedUrl}});
        if (isStale()) {
          setStatus("idle");
          return;
        }
        if (response.error) {
          setStatus("error");
          return;
        }
        const brand = response.data?.statusPageBrandLookup ?? null;
        // Distinguish a genuine miss ("empty") from an operational failure ("error") using the
        // server's typed outcome, rather than inferring it from the absence of assets.
        if (!brand || brand.outcome === "UNAVAILABLE") {
          setStatus("error");
          return;
        }
        if (brand.outcome === "NOT_FOUND") {
          setStatus("empty");
          return;
        }
        applyBrandToForm(brand, setValue);
        setStatus("applied");
      } catch {
        setStatus(isStale() ? "idle" : "error");
      }
    },
    [getValues, runQuery, setValue]
  );

  return {status, queriedUrl, detect};
};
